import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';

import { applyNativeValidity } from '@shared/forms/nativeValidity';
import { ErrorBanner } from '@shared/ui/ErrorBanner';
import { PASSWORD_REVEAL_PADDING, PasswordReveal } from '@shared/ui/PasswordReveal';
import { cn } from '@shared/ui/cn';
import { FieldErrorIcon } from '@shared/ui/icons';

import { authCopy } from './copy';

/**
 * The parts all three pre-session screens compose. Every one binds the design
 * system's own anatomy — `.field`, `.field__label`, `.field__control`,
 * `.field__hint`, `.field__error`, `.btn`, `[data-auth-actions]` — through the
 * token layer rather than restating a value.
 */

/** «الحقول المعلَّمة بـ* مطلوبة» — the rule a red asterisk cannot carry alone. */
export function RequiredNote() {
  return (
    <p className="mb-4 max-w-none text-xs text-ink-muted">
      {authCopy.requiredNote}
      <span className="ms-[2px] text-danger">*</span>
      {authCopy.requiredNoteTail}
    </p>
  );
}

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** Draws the asterisk **and** sets `required` — the two never diverge. */
  requiredMark?: boolean;
  hint?: ReactNode;
  /** Present ⇒ the error is shown, described, and the control is invalid. */
  error?: string;
  /** The mono, letter-spaced, centred treatment the one-time code takes. */
  codeInput?: boolean;
  /**
   * Draws the show/hide toggle inside the control.
   *
   * Opt-in rather than inferred from `type="password"`: a field can be a
   * password field and still have nothing worth revealing — a stored secret
   * being rotated is written once and never read back — and a toggle over a
   * value the reader cannot verify is a control that does nothing.
   */
  revealable?: boolean;
}

/**
 * One field: label above the control — never floating, never inside it — a hint
 * that survives typing, and an error that joins `aria-describedby` when it
 * appears and **leaves when it goes**, so no hidden text is ever announced.
 *
 * The error does not replace the hint. A field that swaps its guidance for its
 * failure takes away the sentence that would have prevented the failure.
 *
 * The last field before the action row takes a group separator rather than a
 * field separator, expressed the way the pack expresses it — the button is not
 * the next field.
 */
export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  {
    label,
    requiredMark = false,
    hint,
    error,
    codeInput = false,
    revealable = false,
    id,
    className,
    ...props
  },
  ref,
) {
  const generatedId = useId();
  const controlId = id ?? generatedId;
  const hintId = `${controlId}-hint`;
  const errorId = `${controlId}-error`;

  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  const control = (type: InputHTMLAttributes<HTMLInputElement>['type']) => (
    <input
      ref={ref}
      id={controlId}
      required={requiredMark}
      aria-required={requiredMark || undefined}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy || undefined}
      className={cn(
        // `line-height: normal` rather than the body's 1.8: a control is one
        // line of typing, and inheriting a prose leading makes the box eight
        // pixels taller than the design system's field without making it any
        // more readable.
        'min-h-[2.75rem] w-full rounded-sm border border-border-strong bg-surface-raised px-4 py-3 text-ink [font-size:var(--fs-body)] [line-height:normal]',
        'transition-[border-color,background-color] duration-fast ease-standard',
        'hover:border-ink-muted focus-visible:border-accent',
        // A machine identifier: mono, isolated, and the one place a Latin
        // tracking value is permitted — it is Latin text, not Arabic.
        codeInput && 'text-center font-mono text-h3 tracking-[.3em] [direction:ltr] [unicode-bidi:isolate]',
        revealable && PASSWORD_REVEAL_PADDING,
        className,
      )}
      {...props}
      // **After the spread, deliberately.** These two are the surface's own
      // rule rather than a default a caller may replace: the browser's refusal
      // must be Arabic on every field, so a caller's own handler is chained
      // rather than allowed to silently take the slot.
      //
      // «Please fill out this field.» in a Latin bubble on an Arabic-only RTL
      // console is the browser's UI locale speaking over the product's.
      // `onInvalid` replaces the text before the bubble is drawn; `onInput`
      // clears it, because a custom message left behind is itself an invalid
      // state and would make a corrected field refuse to submit.
      onInvalid={(event) => {
        applyNativeValidity(event.currentTarget);
        props.onInvalid?.(event);
      }}
      onInput={(event) => {
        event.currentTarget.setCustomValidity('');
        props.onInput?.(event);
      }}
      type={type}
    />
  );

  return (
    <div className="mb-4 block max-w-none [&:has(+[data-auth-actions])]:mb-5">
      <label className="mb-2 block text-sm font-medium" htmlFor={controlId}>
        {label}
        {requiredMark ? (
          <span className="ms-[2px] text-danger" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>

      {revealable ? <PasswordReveal>{(type) => control(type)}</PasswordReveal> : control(props.type)}

      {hint ? (
        <p id={hintId} className="mb-0 mt-2 block max-w-none text-xs text-ink-muted">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} className="mb-0 mt-2 flex max-w-none items-center gap-1 text-xs text-danger">
          <FieldErrorIcon className="size-[14px] shrink-0" />
          {error}
        </p>
      ) : null}
    </div>
  );
});

/**
 * The action row. Full-width at every tier, because the touch target must not
 * shrink to half a line on a 320 px window, and because these screens carry one
 * action each.
 */
export function ActionRow({ children }: { children: ReactNode }) {
  return (
    <div data-auth-actions className="flex w-full flex-wrap gap-3">
      {children}
    </div>
  );
}

type ButtonTone = 'primary' | 'secondary' | 'ghost';

const TONE_CLASSES: Record<ButtonTone, string> = {
  primary: 'border-accent bg-accent text-accent-ink hover:bg-accent-hover active:bg-accent-press',
  secondary: 'border-border-strong bg-surface-raised text-ink hover:bg-surface-overlay',
  ghost: 'border-transparent bg-transparent text-ink hover:bg-hover-bg',
};

/**
 * The type scale is bound as a **property** rather than through the `text-*`
 * utility, and that is not a style preference.
 *
 * `tailwind-merge` classifies a class by its own scale lists, and this repo's
 * font sizes are project tokens (`text-body`, `text-sm`, `text-xs`) that its
 * default list does not carry. It therefore reads them as *colours*, puts them
 * in the same group as `text-accent-ink`, and drops whichever came first — so a
 * brand-filled button silently rendered its label in body ink on the brand
 * fill. Binding the size to its custom property takes it out of the group
 * without taking it off the token.
 */
const SIZE_CLASSES = {
  md: 'min-h-[2.75rem] px-5 py-3 [font-size:var(--fs-sm)]',
  lg: 'min-h-[3.25rem] px-6 py-4 [font-size:var(--fs-body)]',
  sm: 'min-h-[2rem] px-3 py-2 [font-size:var(--fs-xs)]',
} as const;

/**
 * A button in the pack's own anatomy. **One primary per view** — on these
 * screens the primary is the identity action, so the second method is
 * secondary and the resend handle is a ghost.
 *
 * A disabled button always has its reason written beside it; that sentence
 * belongs to the screen, not to this component, which is why nothing here
 * renders one.
 */
export function AuthButton({
  tone = 'primary',
  size = 'md',
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: ButtonTone;
  size?: keyof typeof SIZE_CLASSES;
  children: ReactNode;
}) {
  return (
    <button
      className={cn(
        'inline-flex w-full items-center justify-center gap-2 rounded-sm border font-sans font-semibold leading-tight',
        'active:translate-y-px transition-[background-color,border-color,color] duration-fast ease-standard',
        'disabled:cursor-not-allowed disabled:opacity-45 disabled:active:translate-y-0',
        TONE_CLASSES[tone],
        SIZE_CLASSES[size],
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * The form-level failure.
 *
 * Focus moves to it when it appears, because an operator who pressed submit is
 * looking at the button, and the sentence explaining the refusal is above it.
 * The `tabIndex={-1}` wrapper is what makes that possible without putting a
 * banner in the tab order for the rest of the session.
 */
export function FormError({
  message,
  tone,
  onRetry,
}: {
  message: string;
  tone: 'neutral' | 'danger';
  onRetry?: () => void;
}) {
  const holder = useRef<HTMLDivElement>(null);

  useEffect(() => {
    holder.current?.focus();
  }, [message]);

  return (
    <div ref={holder} tabIndex={-1} className="mb-5 outline-none">
      <ErrorBanner
        message={message}
        unaffected={authCopy.unaffected}
        tone={tone}
        {...(onRetry ? { onRetry } : {})}
      />
    </div>
  );
}

/**
 * The success notice.
 *
 * It is **revealed before its text is rewritten** — a live region revealed and
 * populated in the same frame is announced unreliably, which is the whole
 * reason this is a mounted region rather than a conditional one. It is a
 * banner, never a dialog: nothing is being decided.
 */
export function FormStatus({ message }: { message: string | null }) {
  return (
    <p
      role="status"
      className={cn(
        'mb-5 max-w-none rounded-md border border-s-4 border-border border-s-success bg-success-soft p-4 text-sm text-ink',
        !message && 'hidden',
      )}
    >
      {message}
    </p>
  );
}

/** The one line under the card — the way out of this screen into a sibling. */
export function AuthFoot({ children }: { children: ReactNode }) {
  return <p className="mb-0 mt-4 max-w-none text-xs">{children}</p>;
}

/**
 * Whether the browser believes it has a connection.
 *
 * The destructive triggers elsewhere in the console are swapped first when this
 * goes false; here there is one submit per screen, and swapping its label for
 * the offline sentence is the whole affordance. `navigator.onLine` is a hint
 * rather than a guarantee — a captive portal reports `true` — so it only ever
 * *adds* an explanation, and never becomes the reason a request is not sent.
 */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));

  useEffect(() => {
    function sync() {
      setOnline(navigator.onLine);
    }

    window.addEventListener('online', sync);
    window.addEventListener('offline', sync);
    return () => {
      window.removeEventListener('online', sync);
      window.removeEventListener('offline', sync);
    };
  }, []);

  return online;
}
